import type { ReactNode } from 'react';
import { Box, Icon, Badge, Paragraph, Stack } from '@customerio/pluma-components/react';
import { type Plan, Plans } from '@/services/billing/plans';
import type { Highlight } from '@/services/billing/types';
import { formatNumber } from '@/utils/number';
import type { OrdwayPlanBillingPlan } from '@/services/billing/transforms';
import { TrialExpiryDescription } from './trial-expiry-description';
import { CtaButton } from './cta-button';
import { PlanHighlights } from './plan-highlights';
import { type CheckoutPinnedPrice, PlanNameHeader } from './plan-name-header';
import type { PlanInfoModel } from '../types';
import * as styles from './plan-info.css';

type PlanInfoProps = {
  plan: Plan;
  ordwayPlan?: OrdwayPlanBillingPlan;
  model: PlanInfoModel;
  onLetsTalk: (planType: Plan['planType']) => void;
  onCtaClick: (text: string, planType: Plan['planType']) => void;
  /** Checkout mode: hide the CTA/expiration label and show the checkout pricing note (the checkout page). */
  showCheckout?: boolean;
  /** Force the card's selected/highlighted styling (the checkout summary highlights the chosen plan). */
  isSelected?: boolean;
  /** In checkout, the pinned plan's actual price to show in the header instead of the static price. */
  checkoutPrice?: CheckoutPinnedPrice;
  /** Hides the label that the account state would otherwise show. */
  hideExpirationLabel?: boolean;
};

type CheckoutPricingNoteProps = {
  plan: Plan;
  newPipelinesObjectsPricing: boolean;
};

/**
 * The checkout-only pricing explainer beneath a plan card: the included profiles, the object-overage
 * note (pre-new-pricing), and how usage overages are billed. Ports the Ember plan-info checkout block.
 */
function CheckoutPricingNote({ plan, newPipelinesObjectsPricing }: CheckoutPricingNoteProps) {
  const includedProfiles = formatNumber(plan.includedProfiles);

  return (
    <Stack gap="100" p="150">
      <Paragraph text="product-pxs-active">
        The {plan.name} plan includes {includedProfiles} profiles. If you have or need more than {includedProfiles}{' '}
        profiles, you can still check out.
      </Paragraph>
      {newPipelinesObjectsPricing ? null : (
        <Paragraph text="product-pxs-active">
          When you exceed {formatNumber(plan.includedObjects)} objects there will be an additional usage cost of $
          {formatNumber(plan.objectOverageRate)} per object profile.
        </Paragraph>
      )}
      <Paragraph text="product-pxs">
        We determine your monthly bill based on your usage over the past month and bill any additional usage on top of
        your base price.
      </Paragraph>
      <Paragraph text="product-pxs">
        Additional profiles above the included {includedProfiles} will be billed as overages at $
        {formatNumber(plan.profileOverageRate)} each.
      </Paragraph>
    </Stack>
  );
}

/** Whether the account is subscribed to (or pending checkout for) this plan. */
function isSubscribedToPlan(plan: Plan, model: PlanInfoModel): boolean {
  const activeMatch = model.hasActiveSubscription && model.currentPlanType === plan.planType;
  const pendingMatch = model.isPendingCheckout && model.futurePlanType === plan.planType;
  return activeMatch || pendingMatch;
}

/** The base highlights, preferring the new-pipelines-pricing variant when the flag is on. */
function baseHighlights(plan: Plan, model: PlanInfoModel): Highlight[] {
  if (model.newPipelinesObjectsPricing && plan.highlightsNewPipelinesObjectsPricing != null) {
    return plan.highlightsNewPipelinesObjectsPricing;
  }
  return plan.highlights;
}

/**
 * The plan's highlights: the base list, plus the Builder-upgrade highlights for a Builder-plan account.
 */
function planHighlights(plan: Plan, model: PlanInfoModel): Highlight[] {
  const builderHighlights = model.isBuilder ? (plan.builderUpgradeHighlights ?? []) : [];
  return [...baseHighlights(plan, model), ...builderHighlights];
}

/** Whether the trial/expiration label is suppressed for this account. */
function hideExpirationLabel(model: PlanInfoModel): boolean {
  return model.hasActiveSubscription || model.isPendingCheckout || model.hasFutureSubscription || model.isBuilder;
}

/** The static (non-trial) expiration text: nothing while active/pending, else the trial-expired/summary copy. */
function expirationLabelText(model: PlanInfoModel): string {
  if (model.hasActiveSubscription || model.isPendingCheckout) {
    return '';
  }
  if (model.trialExpired) {
    return 'Your trial has expired';
  }
  return model.planSummary;
}

type ExpirationLabelBodyProps = {
  model: PlanInfoModel;
};

/** The trial/expiration label body: the live trial countdown while on trial, else the static text. */
function ExpirationLabelBody({ model }: ExpirationLabelBodyProps): ReactNode {
  if (model.isBillingTrial) {
    return (
      <TrialExpiryDescription
        trialExpired={model.trialExpired}
        isPendingCheckout={model.isPendingCheckout}
        daysLeftInTrial={model.daysLeftInTrial}
        trialExpiresAt={model.trialExpiresAt}
        canViewBilling={model.canViewBilling}
      />
    );
  }
  return expirationLabelText(model);
}

type PlanCardActionsProps = {
  plan: Plan;
  ordwayPlan?: OrdwayPlanBillingPlan;
  model: PlanInfoModel;
  isSubscribed: boolean;
  onLetsTalk: (planType: Plan['planType']) => void;
  onCtaClick: (text: string, planType: Plan['planType']) => void;
};

/** The card's action area: the "Your Plan" marker when subscribed, otherwise the primary CTA. */
function PlanCardActions({ plan, ordwayPlan, model, isSubscribed, onLetsTalk, onCtaClick }: PlanCardActionsProps) {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" mt="200">
      {isSubscribed ? (
        <Box as="span" className={styles.yourPlanBadge} data-test-selected-plan-label>
          <Icon name="check" size="sm" />
          Your plan
        </Box>
      ) : (
        <CtaButton plan={plan} ordwayPlan={ordwayPlan} model={model} onLetsTalk={onLetsTalk} onCtaClick={onCtaClick} />
      )}
    </Box>
  );
}

type PlanExpirationLabelProps = {
  plan: Plan;
  model: PlanInfoModel;
  isCdpPlan: boolean;
};

/** The trial/expiration label overlay, or null when this plan/account state shouldn't show one. */
function PlanExpirationLabel({ plan, model, isCdpPlan }: PlanExpirationLabelProps) {
  const showLabelForEssentials = plan.name === Plans.Essentials.name && !model.isPremiumTrial;
  const showLabelForPremium = plan.planType === Plans.Premium.planType && model.isPremiumTrial;
  if (isCdpPlan || hideExpirationLabel(model) || !(showLabelForEssentials || showLabelForPremium)) {
    return null;
  }

  return (
    <Box position="absolute" w="full" mt="100" display="flex" justifyContent="center" style={{ top: 0, left: 0 }}>
      {/* Label (not Badge) — only Label exposes the semantic yellow/red trial colors. */}
      <Badge color={model.trialExpired ? 'red' : 'yellow'}>
        <ExpirationLabelBody model={model} />
      </Badge>
    </Box>
  );
}

/**
 * A single plan card: a header (name/price/description) with a highlight badge when subscribed, the
 * primary CTA (or a "Your Plan" marker), an optional trial/expiration label, and the plan highlights.
 * In checkout mode the CTA and expiration label are hidden and the checkout pricing note is shown.
 */
export function PlanInfo({
  plan,
  ordwayPlan,
  model,
  onLetsTalk,
  onCtaClick,
  showCheckout,
  isSelected,
  checkoutPrice,
  hideExpirationLabel: isExpirationLabelHidden,
}: PlanInfoProps) {
  const isSubscribed = isSubscribedToPlan(plan, model);
  const isCdpPlan = plan.planType === Plans.CdpStarter.planType || plan.planType === Plans.CdpPremium.planType;
  const isHighlighted = isSubscribed || isSelected === true;
  const cardClassName = isHighlighted ? `${styles.planInfoCard} ${styles.planInfoCardSelected}` : styles.planInfoCard;
  const inCheckout = showCheckout === true;
  const showExpirationLabel = !inCheckout && isExpirationLabelHidden !== true;

  return (
    <Box className={cardClassName} data-test-plan-info={plan.name}>
      <div className={styles.planInfoHeader}>
        {isHighlighted ? (
          <Box className={styles.planInfoCardSelectedIcon}>
            <Icon name="check" size="md" mt="025" />
          </Box>
        ) : null}
        <PlanNameHeader
          plan={plan}
          startDateInFuture={model.startDateInFuture}
          checkoutPrice={checkoutPrice}
          newPipelinesObjectsPricing={model.newPipelinesObjectsPricing}
        />
        {inCheckout ? null : (
          <PlanCardActions
            plan={plan}
            ordwayPlan={ordwayPlan}
            model={model}
            isSubscribed={isSubscribed}
            onLetsTalk={onLetsTalk}
            onCtaClick={onCtaClick}
          />
        )}
      </div>

      {showExpirationLabel ? <PlanExpirationLabel plan={plan} model={model} isCdpPlan={isCdpPlan} /> : null}

      <PlanHighlights highlights={planHighlights(plan, model)} planType={plan.planType} isCdpPlan={isCdpPlan} />

      {inCheckout ? (
        <CheckoutPricingNote plan={plan} newPipelinesObjectsPricing={model.newPipelinesObjectsPricing} />
      ) : null}
    </Box>
  );
}
